export const MEMBER_BUCKETS = [
  "paid",
  "whitelisted",
  "expired_access",
  "active_unpaid",
  "inactive_unpaid",
  "unknown_unpaid",
  "grace_unpaid",
  "exempt_admin",
  "not_in_group",
] as const;

export type MemberBucket = (typeof MEMBER_BUCKETS)[number];

export const WHOP_STATUSES = [
  "trialing",
  "active",
  "past_due",
  "completed",
  "canceled",
  "expired",
  "unresolved",
  "drafted",
  "canceling",
] as const;

export type WhopStatus = (typeof WHOP_STATUSES)[number];

export interface AppSettings {
  inactivityDays: number;
  newMemberGraceDays: number;
  pastDueGraceDays: number;
  maxAutoRemovalsPerRun: number;
  whopSyncMinutes: number;
  telegramSyncMinutes: number;
  autoEnforcementEnabled: boolean;
  previewOnly: boolean;
  /**
   * Hard stop for every live Telegram removal, including manual dashboard
   * actions. Simulated preview jobs remain available while this is true.
   */
  removalsLocked: boolean;
  automationArmedAt: string | null;
  eligibleProductIds: string[];
  eligibleStatuses: WhopStatus[];
  telegramFieldPatterns: string[];
  timezone: string;
}

export const DEFAULT_SETTINGS: AppSettings = {
  inactivityDays: 30,
  newMemberGraceDays: 3,
  pastDueGraceDays: 3,
  maxAutoRemovalsPerRun: 10,
  whopSyncMinutes: 15,
  telegramSyncMinutes: 60,
  autoEnforcementEnabled: false,
  previewOnly: true,
  removalsLocked: true,
  automationArmedAt: null,
  eligibleProductIds: [],
  // This community requires a currently renewing membership. Whop's
  // `completed` status is commonly used for paid-in-full/one-time access and
  // must be reviewed as an exception instead of being counted as a payer.
  eligibleStatuses: ["active", "trialing", "canceling"],
  telegramFieldPatterns: ["telegram", "telegram username", "tg username", "tg handle"],
  timezone: "Europe/London",
};

export interface TelegramMemberRecord {
  id: number;
  telegramUserId: string;
  chatId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  isBot: boolean;
  status: string;
  firstObservedAt: string;
  lastObservedAt: string;
  joinedAt: string | null;
  leftAt: string | null;
  lastActivityAt: string | null;
  activityObservedSince: string;
  activityCount: number;
  lastKickedAt: string | null;
}

export interface TelegramActivityParticipant {
  telegramUserId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  isBot: boolean;
  status: "member" | "administrator" | "creator" | "restricted";
  joinedAt: string | null;
}

export interface TelegramActivityAggregate {
  telegramUserId: string;
  lastAt: string;
  count: number;
}

/**
 * Privacy-limited output of the one-time MTProto history scanner. Message
 * bodies, chat IDs, and Telegram session material are deliberately absent.
 */
export interface TelegramActivityHistoryImport {
  scanVersion: 1;
  importId: string;
  baseline: string;
  observedAt: string;
  days: number;
  messagesScanned: number;
  participants: TelegramActivityParticipant[];
  activity: TelegramActivityAggregate[];
}

export interface TelegramActivityHistoryImportResult {
  duplicate: boolean;
  participants: number;
  activitySendersApplied: number;
  activitySendersIgnored: number;
  messagesScanned: number;
}

export interface WhopMembershipRecord {
  id: string;
  whopUserId: string;
  status: WhopStatus | string;
  productId: string | null;
  productTitle: string | null;
  planId: string | null;
  renewalPeriodEnd: string | null;
  sourceUpdatedAt: string | null;
  missingSince: string | null;
}

export interface WhitelistRecord {
  note: string;
  expiresAt: string | null;
  createdAt: string;
  createdBy: string;
}

export interface IdentityLinkRecord {
  whopUserId: string;
  method: string;
  confidence: string;
  verifiedAt: string | null;
}

export interface MemberClassificationInput {
  member: TelegramMemberRecord;
  link: IdentityLinkRecord | null;
  whitelist: WhitelistRecord | null;
  memberships: WhopMembershipRecord[];
  settings: AppSettings;
  now: Date;
}

export interface MemberClassification {
  bucket: MemberBucket;
  reason: string;
  isPaid: boolean;
  isActive: boolean | null;
  isWhitelisted: boolean;
  canKick: boolean;
  entitledMembershipId: string | null;
}

export interface DashboardMember {
  member: TelegramMemberRecord;
  classification: MemberClassification;
  displayName: string;
  link: IdentityLinkRecord | null;
  whitelist: WhitelistRecord | null;
  whopUser: {
    id: string;
    username: string | null;
    name: string | null;
    email: string | null;
  } | null;
  memberships: WhopMembershipRecord[];
}

export interface IdentitySuggestion {
  telegram: {
    memberId: number;
    telegramUserId: string;
    username: string | null;
    displayName: string;
  };
  whop: {
    userId: string;
    username: string | null;
    name: string | null;
    email: string | null;
    statuses: string[];
    products: string[];
  };
  confidence: "high" | "medium";
  score: number;
  signals: string[];
}

export interface WhopAccessSummary {
  membershipRecords: number;
  entitledMembershipRecords: number;
  nonEntitledMembershipRecords: number;
  entitledCustomers: number;
  linkedEntitledCustomers: number;
  unlinkedEntitledCustomers: number;
  statusCounts: Record<string, number>;
  accessStatuses: WhopStatus[];
  pastDueGraceDays: number;
}

export interface IntegrationStatus {
  configured: boolean;
  healthy: boolean;
  message: string;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
}

export interface DashboardSummary {
  publicUrl: string;
  totalInGroup: number;
  buckets: Record<MemberBucket, number>;
  queuedActions: number;
  integrations: {
    telegramBot: IntegrationStatus;
    telegramInventory: IntegrationStatus;
    telegramActivity: IntegrationStatus;
    whop: IntegrationStatus;
    whopWebhook: IntegrationStatus;
    memberClaims: IntegrationStatus;
  };
  settings: AppSettings;
  whopAccess: WhopAccessSummary;
  lastReconciledAt: string | null;
}
